import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Toast, useModal, Icon } from '@ampol/design-system';
import './toast-alert.scss';
import clsx from 'clsx';
import { addToastAlertModal, resetToastAlert, resetToastAlertModal } from 'actions/toastAlertActions';

/**
 * A ToastAlert component that displays alerts.
 */
const ToastAlert = () => {
  const dispatch = useDispatch();
  const messageRef = useRef(null);

  const message = useSelector((state) => state.toastAlertStore.message);
  const isOpen = useSelector((state) => state.toastAlertStore.isVisible);
  const variant = useSelector((state) => state.toastAlertStore.variant);
  const dismissable = useSelector((state) => state.toastAlertStore.dismissable);
  const positionX = useSelector((state) => state.toastAlertStore.x);
  const positionY = useSelector((state) => state.toastAlertStore.y);
  const isAlertModalOpen = useSelector((state) => state.toastAlertStore.isModalOpen);

  const [isOverflow, setIsOverflow] = useState(false);

  // Check if alert message overflows toast component
  useEffect(() => {
    if (messageRef.current && isOpen) {
      const { scrollWidth, clientWidth } = messageRef.current;
      setIsOverflow(scrollWidth > clientWidth);
    }
  }, [message, isOpen]);

  const formattedMessage = (message) => {
    if (!message) return '';
  
    // Split into sentences
    const [firstLine, ...rest] = message.split('|');
  
    // If more than one sentence exists, format separately
    if (rest.length > 0) {
      return (
        <>
          <strong>{firstLine}</strong>
          <div>{rest.join(' ')}</div>
        </>
      );
    }

    // Otherwise, return plain message
    return message;
  };
  
  const handleOpenModal = () => {
    toastAlertModal.open();
    dispatch(addToastAlertModal());
  };

  const handleCloseModal = () => {
    toastAlertModal.close();
  };

  const toastAlertModal = useModal({
    id: 'toast-alert-modal',
    title: 'Alert',
    content: <>{formattedMessage(message)}</>,
    variant: 'double',
    color: 'blue',
    actions: [
      {
        label: 'Close',
        onClick: handleCloseModal,
      },
    ],
    onClose: () => dispatch(resetToastAlertModal()),
  });

  return (
    <Toast
      open={isOpen}
      portal
      variant={variant}
      x={positionX}
      y={positionY}
      className={clsx('toast-alert__wrapper', { 'toast-alert__hidden': isAlertModalOpen })}
      data-testid={'Alert'}
    >
      <div className={clsx('toast-alert__icon', { 'toast-alert__icon--overflow': isOverflow })}>
        {variant === 'success' && <Icon size={24} url={'/icons/tick.svg'} />}
        {variant === 'neutral' && <Icon size={20} url={'/icons/bell.svg'} />}
        {variant === 'error' && <Icon size={20} url={'/icons/alert.svg'} />}
      </div>
      <div ref={messageRef} className={clsx('toast-alert__message', { 'toast-alert__message--overflow': isOverflow })} data-testid={variant}>
        {formattedMessage(message)}
      </div>
      {isOverflow && (
        <div className={clsx('toast-alert__expand', { 'toast-alert__expand--overflow': isOverflow })}>
          <Button
            label={'Expand'}
            variant={'tertiary'}
            color={'black'}
            className={'toast-alert__expand-button'}
            onClick={handleOpenModal}
          />
        </div>
      )}
      {dismissable && (
        <button
          onClick={() => dispatch(resetToastAlert())}
          aria-label="close toast notification alert"
          className={'toast-alert__dismiss-button'}
          type="button"
        >
          <Icon size={24} url={'/icons/close.svg'} />
        </button>
      )}
    </Toast>
  );
};

export default ToastAlert;
